import { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';

type AdminKol = {
  id: string;
  name: string;
  email: string | null;
  agencyOrg: {
    id: string;
    name: string;
  };
  tags: Array<{ tag: { id: string; name: string } }>;
  createdAt: string;
  platforms: any;
  audience: any;
};

type KolsResponse = {
  items: AdminKol[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
};

const totalFollowers = (kol: AdminKol) => {
  const platforms = kol.platforms || {};
  return Object.values(platforms).reduce((sum: number, p: any) => sum + (p?.followers || 0), 0);
};

const avgEngagement = (kol: AdminKol) => {
  const platforms = kol.platforms || {};
  const values = Object.values(platforms)
    .map((p: any) => Number(p?.engagementRate || 0))
    .filter((v) => Number.isFinite(v) && v > 0);
  if (!values.length) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
};

export default function AdminKOLs() {
  const [items, setItems] = useState<AdminKol[]>([]);
  const [search, setSearch] = useState('');
  const [tags, setTags] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<KolsResponse['pagination']>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.get<KolsResponse>('/admin/kols', {
          params: {
            search: search || undefined,
            tags: tags || undefined,
            page,
            limit: 20,
          },
        });
        if (!active) return;
        setItems(data.items);
        setPagination(data.pagination);
      } catch (err: any) {
        if (!active) return;
        setError(err.response?.data?.error || 'Failed to load KOLs');
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [search, tags, page]);

  const stats = useMemo(() => {
    const visibleFollowers = items.reduce((sum, kol) => sum + totalFollowers(kol), 0);
    const avgEng = items.length
      ? items.reduce((sum, kol) => sum + avgEngagement(kol), 0) / items.length
      : 0;
    return {
      total: pagination.total,
      visible: items.length,
      followers: visibleFollowers,
      avgEng,
    };
  }, [items, pagination.total]);

  return (
    <div className="space-y-6 text-glass-text">
      <div>
        <h1 className="page-title">KOL Management</h1>
        <p className="text-glass-text-2 text-sm mt-1">Global KOL visibility across all agencies</p>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{error}</div>
      )}

      <div className="grid md:grid-cols-4 gap-4">
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Total KOLs</div>
          <div className="stat-display text-2xl mt-1">{stats.total}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Visible Results</div>
          <div className="stat-display text-2xl mt-1 text-accent2">{stats.visible}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Visible Reach</div>
          <div className="stat-display text-2xl mt-1 text-accent2">{stats.followers.toLocaleString()}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Avg Engagement</div>
          <div className="stat-display text-2xl mt-1 text-[rgb(var(--pos))]">{stats.avgEng.toFixed(2)}%</div>
        </div>
      </div>

      <div className="glass-card p-4 grid md:grid-cols-2 gap-4">
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          placeholder="Search KOL name, email, bio..."
          className="glass-input w-full px-4 py-2"
        />
        <input
          type="text"
          value={tags}
          onChange={(e) => {
            setPage(1);
            setTags(e.target.value);
          }}
          placeholder="Filter tags (comma separated, e.g. Beauty,Tech)"
          className="glass-input w-full px-4 py-2"
        />
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-glass bg-glass-2/30">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">KOL</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Agency</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Followers</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Engagement</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Tags</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-glass">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-glass-text-2">
                    Loading KOLs...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-glass-text-2">
                    No KOLs found.
                  </td>
                </tr>
              ) : (
                items.map((kol) => (
                  <tr key={kol.id} className="hover:bg-glass-3/40 transition-colors">
                    <td className="px-4 py-4">
                      <div className="font-medium text-glass-text">{kol.name}</div>
                      <div className="text-sm text-glass-text-3">{kol.email || 'No email'}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">{kol.agencyOrg?.name || 'N/A'}</td>
                    <td className="px-4 py-4 text-sm font-semibold text-glass-text">
                      {totalFollowers(kol).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-sm text-[rgb(var(--pos))]">{avgEngagement(kol).toFixed(2)}%</td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        {kol.tags.slice(0, 4).map((t) => (
                          <span key={t.tag.id} className="status-pill status-pill-info">
                            {t.tag.name}
                          </span>
                        ))}
                        {kol.tags.length > 4 && (
                          <span className="status-pill status-pill-muted">+{kol.tags.length - 4}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-glass flex items-center justify-between text-sm text-glass-text-2">
          <span>
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} KOLs)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
              className="btn-glass px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={pagination.page >= pagination.totalPages}
              className="btn-glass px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
